import { SectionBox } from '@shared/components/ui/SectionBox';
import { faBuilding, faCalendar, faDollarSign, faFileInvoice, faTag } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateWithTime } from '@shared/utils/FormatUtils';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import type { PreInvoice } from '../../models/ViewPreInvoices';

interface PreInvoiceHeaderProps {
  preInvoice: PreInvoice;
  isFetching?: boolean;
}

// Función utilitaria para formatear montos
const formatAmount = (amount: number, currency: string): string => formatCurrencyAmount(amount, currency || 'EUR');

// Función para traducir el estado
const getStatusLabel = (status: string, t: (key: string) => string): string => {
  const statusMap: Record<string, string> = {
    OPEN: t('table.status_open', 'Abierta'),
    CLOSED: t('table.status_closed', 'Facturada'),
    DRAFT: t('table.status_draft', 'Borrador'),
  };
  return statusMap[status] || status;
};

// Función para traducir el período
const getPeriodLabel = (period: string | undefined, t: (key: string) => string): string => {
  if (!period) return '-';
  const periodMap: Record<string, string> = {
    DAILY: t('filters.periodOptions.daily', 'Diario'),
    WEEKLY: t('filters.periodOptions.weekly', 'Semanal'),
    MONTHLY: t('filters.periodOptions.monthly', 'Mensual'),
    QUARTERLY: t('filters.periodOptions.quarterly', 'Trimestral'),
    ANNUAL: t('filters.periodOptions.annual', 'Anual'),
  };
  return periodMap[period.toUpperCase()] || period;
};

// Función para traducir el tipo
const getTypeLabel = (type: string | undefined, t: (key: string) => string): string => {
  if (!type) return '-';
  const typeMap: Record<string, string> = {
    SHIPPING: t('table.type_shipping', 'Envío'),
    RETURN: t('table.type_return', 'Devolución'),
    RESHIPMENT: t('table.type_reshipment', 'Reenvío'),
    STORAGE: t('table.type_storage', 'Almacenamiento'),
  };
  return typeMap[type.toUpperCase()] || type;
};

export const PreInvoiceHeader = ({ preInvoice, isFetching = false }: PreInvoiceHeaderProps) => {
  const { t } = useTranslation('viewPreInvoicesModule');
  const currency = useCurrency();

  const formattedDate = preInvoice.created_at ? formatDateWithTime(preInvoice.created_at) : '-';
  const statusLabel = getStatusLabel(preInvoice.status, t);
  const periodLabel = getPeriodLabel(preInvoice.period, t);
  const typeLabel = getTypeLabel(preInvoice.type, t);
  const isClosed = preInvoice.status === 'CLOSED';

  const cards = [
    {
      title: t('header.code', 'Código'),
      value: preInvoice.code,
      icon: faFileInvoice,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
    {
      title: t('header.created_at', 'Fecha de creación'),
      value: formattedDate,
      icon: faCalendar,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
    {
      title: t('header.status', 'Estado'),
      value: statusLabel,
      icon: faBuilding,
      iconBgClass: 'bg-card-border',
      iconTextClass: isClosed ? 'text-success' : 'text-icons',
      valueClassName: isClosed ? 'text-success' : '',
    },
    {
      title: t('header.total', 'Total'),
      value: formatAmount(preInvoice.total_amount, currency),
      icon: faDollarSign,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
  ];

  // Si hay tipo, agregar card de tipo
  if (preInvoice.type) {
    cards.splice(3, 0, {
      title: t('header.type', 'Tipo'),
      value: typeLabel,
      icon: faTag,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    });
  }

  // Si hay período, agregar card de período
  if (preInvoice.period) {
    cards.splice(preInvoice.type ? 4 : 3, 0, {
      title: t('header.period', 'Período'),
      value: periodLabel,
      icon: faCalendar,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    });
  }

  const totalCards = cards.length;
  const gridColsClass = totalCards === 5 ? 'md:grid-cols-5' : totalCards === 6 ? 'md:grid-cols-6' : 'md:grid-cols-4';

  return (
    <div className="mb-6">
      <div className={`grid grid-cols-1 ${gridColsClass} gap-4`}>
        {cards.map((card, index) => (
          <SectionBox key={index} className="">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm text-subtext mb-1">{card.title}</h3>
                <p className={`text-md text-title ${card.valueClassName || ''}`}>
                  {isFetching ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="animate-pulse">...</span>
                    </span>
                  ) : (
                    card.value
                  )}
                </p>
              </div>
              <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${card.iconBgClass}`}>
                <FontAwesomeIcon icon={card.icon} className={`w-4 h-4 ${card.iconTextClass}`} />
              </div>
            </div>
          </SectionBox>
        ))}
      </div>
    </div>
  );
};
